import React from "react";
import { fetchInvoice } from "../api/billingApi.js";
import { consolidationRequest } from "../api/consolidationApi.js";
import { createOrderRefreshQueue, subscribeOrderDataChange } from "../utils/orderDataSync.js";

// Refresh the list AND the open document, without replacing unsaved invoice edits.
export default function useBillingLiveRefresh({ enabled, dirty = false, canManageDrafts = false, invoice, setInvoice, refreshView, onError }) {
  const latest = React.useRef();
  latest.current = { enabled, dirty, canManageDrafts, invoice, setInvoice, refreshView, onError };
  const queue = React.useRef(null);
  const running = React.useRef(false);
  const mounted = React.useRef(true);
  const attempted = React.useRef(new Set());
  const failures = React.useRef(new Map());
  const pendingDetail = React.useRef(false);
  const [notice, setNotice] = React.useState({});
  const [completedOperations, setCompletedOperations] = React.useState(0);
  const opening = React.useRef({ id: invoice?.id, token: 0 });
  if (opening.current.id !== invoice?.id) opening.current = { id: invoice?.id, token: opening.current.token + 1 };
  const notify = React.useCallback((patch, token = opening.current.token) => {
    if (mounted.current && token === opening.current.token) {
      setNotice(previous => ({ ...(previous.token === token ? previous : {}), token, ...patch }));
    }
  }, []);
  const accept = React.useCallback((fresh, before, token) => {
    if (!mounted.current || token !== opening.current.token || latest.current.invoice?.id !== before.id) return;
    if (latest.current.dirty || !latest.current.enabled) {
      pendingDetail.current = true;
      notify({ deferred: true, conflict: fresh.version !== before.version }, token);
      return;
    }
    const calculatedLines = value => JSON.stringify((value.lines || []).filter(line => line.billable_item_id)
      .map(line => [line.billable_item_id, line.quantity, line.unit_rate, line.line_total]));
    const updated = fresh.billing_origin === "consolidation" && fresh.consolidation_refresh?.state === "current"
      && fresh.version > before.version && calculatedLines(fresh) !== calculatedLines(before);
    latest.current.setInvoice(shown => shown?.id === before.id && shown.version === before.version && fresh.version >= shown.version ? fresh : shown);
    pendingDetail.current = false;
    const sourceStateChanged = fresh.consolidation_refresh?.state !== before.consolidation_refresh?.state;
    notify({ deferred: false, conflict: false,
      ...(fresh.consolidation_refresh?.state === "current" || (sourceStateChanged && fresh.consolidation_refresh?.state !== "ready") ? { error: "" } : {}),
      ...(updated ? { updated: true, error: "" } : {}) }, token);
  }, [notify]);

  const synchronize = React.useCallback(async (manual = false) => {
    const before = latest.current.invoice;
    if (!before || running.current || !latest.current.enabled || latest.current.dirty) return;
    const token = opening.current.token;
    running.current = true;
    notify({ updating: true, error: "", updated: false }, token);
    try {
      let fresh = manual ? await fetchInvoice(before.id) : before;
      if (!mounted.current || token !== opening.current.token || latest.current.dirty || !latest.current.enabled) {
        accept(fresh, before, token);
        return;
      }
      const key = `${fresh.id}:${fresh.version}`;
      if (latest.current.canManageDrafts && fresh.lifecycle_status === "draft"
        && fresh.consolidation_refresh?.state === "ready" && (manual || !attempted.current.has(key))) {
        attempted.current.add(key);
        await consolidationRequest(`/invoices/${fresh.id}/regenerate`, {
          method: "POST", body: { version: fresh.version, shared_versions: {} },
        });
        if (!mounted.current) return;
        fresh = await fetchInvoice(fresh.id);
        if (fresh.consolidation_refresh?.state === "ready") {
          const message = "Le brouillon reste à actualiser. Réessayez la mise à jour.";
          attempted.current.add(`${fresh.id}:${fresh.version}`);
          failures.current.set(`${fresh.id}:${fresh.version}`, message);
          notify({ error: message }, token);
        } else {
          failures.current.delete(key);
          notify({ updated: fresh.consolidation_refresh?.state === "current" }, token);
        }
      }
      accept(fresh, before, token);
      if (mounted.current) void latest.current.refreshView();
    } catch (error) {
      const message = error.message || "La mise à jour du brouillon n’a pas abouti.";
      failures.current.set(`${before.id}:${before.version}`, message);
      notify({ error: message }, token);
      if (mounted.current && error.status === 409) {
        try {
          const fresh = await fetchInvoice(before.id);
          // A conflict is reviewed, never automatically retried against another version.
          attempted.current.add(`${fresh.id}:${fresh.version}`);
          failures.current.set(`${fresh.id}:${fresh.version}`, message);
          accept(fresh, before, token);
        } catch { /* Keep the original failure and offer an explicit retry. */ }
      }
    } finally {
      running.current = false;
      if (mounted.current) setCompletedOperations(value => value + 1);
      notify({ updating: false }, token);
      queue.current?.resume();
    }
  }, [accept, notify]);

  React.useEffect(() => {
    let disposed = false;
    mounted.current = true;
    const current = createOrderRefreshQueue({
      enabled: () => latest.current.enabled && !running.current && document.visibilityState !== "hidden",
      refresh: async () => {
        const before = latest.current.invoice;
        const token = opening.current.token;
        await Promise.all([
          latest.current.refreshView(),
          before ? fetchInvoice(before.id).then((fresh) => {
            if (disposed || token !== opening.current.token) return;
            accept(fresh, before, token);
          // Le détail déplié affiche l'échec avec « Réessayer la mise à jour » : la page et la liste restent en place.
          }).catch(error => {
            notify({ error: error.message || "Impossible d’actualiser la facture ouverte." }, token);
          }) : undefined,
        ]);
      },
      onError: (error) => {
        latest.current.onError(error);
      },
    });
    queue.current = current;
    const unsubscribe = subscribeOrderDataChange(() => {
      if (latest.current.dirty && latest.current.invoice) {
        pendingDetail.current = true;
        notify({ deferred: true });
      }
      current.invalidate();
    });
    const activate = () => {
      if (document.visibilityState !== "hidden") current.invalidate();
    };
    window.addEventListener("focus", activate);
    document.addEventListener("visibilitychange", activate);
    return () => {
      disposed = true;
      mounted.current = false;
      unsubscribe(); current.dispose();
      window.removeEventListener("focus", activate);
      document.removeEventListener("visibilitychange", activate);
    };
  }, [accept, notify]);
  React.useEffect(() => {
    if (enabled && !dirty && pendingDetail.current) queue.current?.invalidate();
    queue.current?.resume();
  }, [enabled, dirty]);
  React.useEffect(() => {
    if (enabled && !dirty && canManageDrafts && invoice?.lifecycle_status === "draft"
      && invoice.consolidation_refresh?.state === "ready" && !attempted.current.has(`${invoice.id}:${invoice.version}`)) {
      void synchronize();
    }
  }, [enabled, dirty, canManageDrafts, invoice, synchronize, completedOperations]);
  const currentNotice = notice.token === opening.current.token ? notice : {};
  const rememberedError = invoice?.consolidation_refresh?.state === "ready" ? failures.current.get(`${invoice.id}:${invoice.version}`) : "";
  return { ...currentNotice, error: currentNotice.error || (!currentNotice.updating && rememberedError), retry: () => synchronize(true), reportConflict: error => {
    if (error?.status === 409) {
      pendingDetail.current = true;
      notify({ conflict: true, deferred: true, error: "Le relevé a changé. Vos saisies sont conservées. Annulez les changements pour charger sa nouvelle version." });
      queue.current?.invalidate();
    }
  } };
}
